/**
 * Support API — feedback + two-way support tickets.
 *
 * All calls hit main-server REST with the player's JWT (issued in the
 * auth:success payload) as a Bearer header, matching diceStoreApi / avatarApi.
 * Attachments (screenshots / screen recordings) are sent as base64 data URLs;
 * main-server pushes them to Bunny CDN under support/<ticketId>/<id>.<ext>.
 */

import { serverConfig } from '../network/ServerConfig';

export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

export interface TicketAttachment {
  id: string;
  url: string;
  kind: 'image' | 'video';
  contentType: string;
}

export interface TicketMessage {
  authorRole: 'player' | 'admin';
  body: string;
  attachments: TicketAttachment[];
  createdAt: string;
}

export interface TicketSummary {
  id: string;
  subject: string;
  status: TicketStatus;
  unreadForPlayer: boolean;
  messageCount: number;
  createdAt: string;
  lastMessageAt: string;
}

export interface TicketDetail {
  id: string;
  subject: string;
  status: TicketStatus;
  category?: string;
  unreadForPlayer: boolean;
  createdAt: string;
  lastMessageAt: string;
  messages: TicketMessage[];
}

function authHeaders(token: string): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

/** Submit free-text feedback. Resolves true on success. */
export async function submitFeedback(
  token: string,
  message: string,
  meta: { appVersion?: string; platform?: string; deviceInfo?: string } = {},
): Promise<boolean> {
  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/feedback`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ message, ...meta }),
    });
    return resp.ok;
  } catch {
    return false;
  }
}

/** Open a new ticket. Returns the created ticket, or null on failure. */
export async function createTicket(
  token: string,
  subject: string,
  body: string,
  attachments: string[] = [],
): Promise<TicketDetail | null> {
  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/support/tickets`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ subject, body, attachments }),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { ticket: TicketDetail };
    return data.ticket;
  } catch {
    return null;
  }
}

/** List the player's own tickets (newest first). */
export async function listTickets(token: string): Promise<TicketSummary[]> {
  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/support/tickets`, {
      headers: authHeaders(token),
    });
    if (!resp.ok) return [];
    const data = (await resp.json()) as { tickets: TicketSummary[] };
    return data.tickets ?? [];
  } catch {
    return [];
  }
}

/** Fetch one ticket's full thread (also clears the player unread flag server-side). */
export async function getTicket(token: string, id: string): Promise<TicketDetail | null> {
  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/support/tickets/${id}`, {
      headers: authHeaders(token),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { ticket: TicketDetail };
    return data.ticket;
  } catch {
    return null;
  }
}

/** Reply to a ticket. Returns the updated ticket, or null on failure. */
export async function replyTicket(
  token: string,
  id: string,
  body: string,
  attachments: string[] = [],
): Promise<TicketDetail | null> {
  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/support/tickets/${id}/messages`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ body, attachments }),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { ticket: TicketDetail };
    return data.ticket;
  } catch {
    return null;
  }
}

/** Close a ticket. Returns the updated ticket, or null on failure. */
export async function closeTicket(token: string, id: string): Promise<TicketDetail | null> {
  return ticketAction(token, id, 'close');
}

/** Reopen a resolved/closed ticket. Returns the updated ticket, or null on failure. */
export async function reopenTicket(token: string, id: string): Promise<TicketDetail | null> {
  return ticketAction(token, id, 'reopen');
}

async function ticketAction(token: string, id: string, action: 'close' | 'reopen'): Promise<TicketDetail | null> {
  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/support/tickets/${id}/${action}`, {
      method: 'POST',
      headers: authHeaders(token),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { ticket: TicketDetail };
    return data.ticket;
  } catch {
    return null;
  }
}
